(() => {
  const introKey = 'kurogane-intro-20260720-entered';
  const focusableSelector =
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const safeSessionGet = (key) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const safeSessionSet = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      // Session storage is optional.
    }
  };

  const initIntro = () => {
    const intro = document.querySelector('[data-steel-intro]');
    if (!intro) return;

    const skip =
      intro.dataset.steelDesignMode === 'true' ||
      prefersReducedMotion() ||
      safeSessionGet(introKey) === '1' ||
      new URLSearchParams(window.location.search).has('inside') ||
      new URLSearchParams(window.location.search).has('steel-study');

    if (skip) return;

    intro.hidden = false;
    document.body.classList.add('steel-intro-open');
    intro.focus({ preventScroll: true });

    let leaving = false;
    let touchStart = null;

    const enter = () => {
      if (leaving) return;
      leaving = true;
      safeSessionSet(introKey, '1');
      intro.classList.add('is-leaving');
      const delay = prefersReducedMotion() ? 0 : 720;
      window.setTimeout(() => {
        intro.hidden = true;
        document.body.classList.remove('steel-intro-open');
      }, delay);
    };

    intro.querySelector('[data-steel-enter]')?.addEventListener('click', enter);
    intro.addEventListener('wheel', (event) => {
      if (event.deltaY > 18) {
        event.preventDefault();
        enter();
      }
    }, { passive: false });
    intro.addEventListener('touchstart', (event) => {
      touchStart = event.touches[0]?.clientY ?? null;
    }, { passive: true });
    intro.addEventListener('touchend', (event) => {
      const touchEnd = event.changedTouches[0]?.clientY;
      if (touchStart !== null && touchEnd !== undefined && touchStart - touchEnd > 36) {
        enter();
      }
      touchStart = null;
    }, { passive: true });
    intro.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'PageDown' || event.key === 'Enter') {
        enter();
      }
    });
  };

  const initMenu = () => {
    document.querySelectorAll('[data-steel-menu]').forEach((menu) => {
      const summary = menu.querySelector('summary');
      if (!summary || menu.dataset.steelMenuReady === 'true') return;

      menu.dataset.steelMenuReady = 'true';
      summary.setAttribute('aria-expanded', menu.open ? 'true' : 'false');
      menu.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', menu.open ? 'true' : 'false');
      });
      menu.querySelectorAll('[data-steel-menu-link]').forEach((link) => {
        link.addEventListener('click', () => menu.removeAttribute('open'));
      });
      menu.querySelector('[data-steel-menu-close]')?.addEventListener('click', () => {
        menu.removeAttribute('open');
        summary.focus();
      });
    });
  };

  const initHeader = () => {
    const header = document.querySelector('[data-steel-header]');
    if (!header) return;

    const update = () => {
      header.classList.toggle('is-solid', window.scrollY > window.innerHeight * 0.72);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
  };

  const initReveals = () => {
    document.documentElement.classList.add('steel-js-ready');
    const elements = Array.from(document.querySelectorAll('[data-reveal]'));
    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        currentObserver.unobserve(entry.target);
      });
    }, { threshold: 0.12 });

    elements.forEach((element) => observer.observe(element));
  };

  const initStudySwaps = () => {
    document.querySelectorAll('[data-steel-swap]').forEach((button) => {
      button.addEventListener('click', () => {
        const card = button.closest('[data-steel-study-card]');
        if (!card) return;
        const isAlternate = card.classList.toggle('is-alt');
        button.setAttribute('aria-pressed', isAlternate ? 'true' : 'false');
        const name = card.querySelector('.product-card__info strong')?.textContent?.trim() || 'estudio';
        button.setAttribute(
          'aria-label',
          isAlternate ? `Mostrar vista principal de ${name}` : `Mostrar vista alterna de ${name}`,
        );
      });
    });
  };

  const createModalController = ({ modal, dialog, content }) => {
    let previousFocus = null;
    let activeSlug = null;

    const getFocusable = () =>
      Array.from(dialog.querySelectorAll(focusableSelector)).filter(
        (element) => !element.hasAttribute('hidden'),
      );

    const show = (slug, updateUrl = true) => {
      const template = document.querySelector(`[data-steel-study-template="${slug}"]`);
      if (!template) return;

      previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      activeSlug = slug;
      content.replaceChildren(template.content.cloneNode(true));
      modal.hidden = false;
      document.body.classList.add('steel-modal-open');
      dialog.setAttribute('aria-labelledby', `steel-study-title-${slug}`);

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('steel-study', slug);
        window.history.pushState({ steelStudy: slug }, '', `${url.pathname}${url.search}${url.hash}`);
      }

      window.requestAnimationFrame(() => {
        getFocusable()[0]?.focus();
      });
    };

    const hide = (updateUrl = true) => {
      if (modal.hidden) return;
      modal.hidden = true;
      content.replaceChildren();
      document.body.classList.remove('steel-modal-open');
      const focusTarget = previousFocus;
      previousFocus = null;
      activeSlug = null;
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.delete('steel-study');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }
      focusTarget?.focus();
    };

    const trapFocus = (event) => {
      if (modal.hidden || event.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    return {
      show,
      hide,
      isOpen: () => !modal.hidden,
      activeSlug: () => activeSlug,
      trapFocus,
    };
  };

  const initStudyModal = () => {
    const modal = document.querySelector('[data-steel-study-modal]');
    const dialog = modal?.querySelector('[data-steel-dialog]');
    const content = modal?.querySelector('[data-steel-modal-content]');
    if (!modal || !dialog || !content) return null;
    return createModalController({ modal, dialog, content });
  };

  const initStatusModal = () => {
    const modal = document.querySelector('[data-steel-status-modal]');
    const dialog = modal?.querySelector('[data-steel-status-dialog]');
    if (!modal || !dialog) return null;

    let previousFocus = null;
    const getFocusable = () =>
      Array.from(dialog.querySelectorAll(focusableSelector)).filter(
        (element) => !element.hasAttribute('hidden'),
      );

    const show = () => {
      previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      modal.hidden = false;
      document.body.classList.add('steel-modal-open');
      window.requestAnimationFrame(() => getFocusable()[0]?.focus());
    };

    const hide = () => {
      if (modal.hidden) return;
      modal.hidden = true;
      document.body.classList.remove('steel-modal-open');
      previousFocus?.focus();
      previousFocus = null;
    };

    const trapFocus = (event) => {
      if (modal.hidden || event.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    modal.addEventListener('mousedown', (event) => {
      if (event.target === modal) hide();
    });

    return { show, hide, isOpen: () => !modal.hidden, trapFocus };
  };

  const initModals = () => {
    const studyModal = initStudyModal();
    const statusModal = initStatusModal();

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const openStudy = target?.closest('[data-steel-study-open]');
      const statusTrigger = target?.closest('[data-steel-status-trigger]');
      const close = target?.closest('[data-steel-modal-close]');

      if (openStudy && studyModal) {
        event.preventDefault();
        studyModal.show(openStudy.dataset.steelStudyOpen);
        return;
      }

      if (statusTrigger && statusModal) {
        event.preventDefault();
        studyModal?.hide();
        statusModal.show();
        return;
      }

      if (close) {
        if (studyModal?.isOpen()) studyModal.hide();
        if (statusModal?.isOpen()) statusModal.hide();
      }
    });

    document.addEventListener('keydown', (event) => {
      studyModal?.trapFocus(event);
      statusModal?.trapFocus(event);
      if (event.key !== 'Escape') return;
      if (studyModal?.isOpen()) studyModal.hide();
      if (statusModal?.isOpen()) statusModal.hide();
    });

    const initialStudy = new URLSearchParams(window.location.search).get('steel-study');
    if (initialStudy && studyModal) studyModal.show(initialStudy, false);

    window.addEventListener('popstate', () => {
      const slug = new URLSearchParams(window.location.search).get('steel-study');
      if (slug && studyModal) {
        studyModal.show(slug, false);
      } else {
        studyModal?.hide(false);
      }
    });
  };

  const init = () => {
    initIntro();
    initMenu();
    initHeader();
    initReveals();
    initStudySwaps();
    initModals();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
